import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import styles from './BillActionStrip.module.css';

const getDraftDate = (draft) => draft?.createdAt || draft?.savedAt || draft?.updatedAt || 0;

const getCustomerLabel = (draft) => {
    const name = String(draft?.customer?.name || '').trim();
    const phone = String(draft?.customer?.phone || '').trim();
    if (name && name.toLowerCase() !== 'draft customer') return name;
    if (phone) return phone;
    return 'New customer';
};

const formatDraftTime = (draft) => {
    const value = getDraftDate(draft);
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(date);
};

/**
 * Open bills strip.
 *   canCreate        show "Create New Bill" (billing.create)
 *   hasCancelAccess  show the x on each draft (billing.cancel_draft)
 * Both default to false, so nothing shows unless access is given.
 */
const BillActionStrip = ({
    drafts = [],
    currentDraftId,
    handleStartNewBill,
    handleOpenDraft,
    handleDeleteDraft,
    hasCancelAccess = false,
    canCreate = false,
}) => {
    const sortedDrafts = useMemo(() => (
        [...drafts].sort((a, b) => new Date(getDraftDate(b)).getTime() - new Date(getDraftDate(a)).getTime())
    ), [drafts]);

    const handleTabKeyDown = (event, draft) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenDraft(draft);
        }
    };

    return (
        <div className={styles.actionStrip} aria-label="Invoice drafts">
            <div className={styles.tabScrollArea}>
                {canCreate && (
                    <>
                        <button type="button" className={styles.initBtn} onClick={handleStartNewBill}>
                            <span className={styles.initIcon}><Plus size={14} strokeWidth={2.5} className={styles.btnIcon} /></span>
                            <span>Create New Bill</span>
                        </button>
                        <span className={styles.dividerX} aria-hidden="true" />
                    </>
                )}

                {sortedDrafts.map((draft) => {
                    const draftId = draft.id || draft._id;
                    const isActive = String(currentDraftId) === String(draftId);
                    const invoiceNumber = draft.invoiceMeta?.invoiceNo || draft.invoiceNo || 'DRAFT';
                    const customerLabel = getCustomerLabel(draft);
                    const draftTime = formatDraftTime(draft);
                    const isDraft = !draft.status || draft.status === 'draft';

                    return (
                        <div
                            key={draftId}
                            role="tab"
                            tabIndex={0}
                            aria-selected={isActive}
                            className={`${styles.sessionTab} ${isActive ? styles.tabActive : ''}`}
                            onClick={() => handleOpenDraft(draft)}
                            onKeyDown={(event) => handleTabKeyDown(event, draft)}
                        >
                            <div className={styles.tabInfo}>
                                <div className={styles.tabHeader}>
                                    <span className={styles.statusMark} aria-hidden="true" />
                                    <span className={styles.tabInvNo}>{invoiceNumber}</span>
                                    {draftTime && (
                                        <>
                                            <span className={styles.separator}>•</span>
                                            <span className={styles.tabTime}>{draftTime}</span>
                                        </>
                                    )}
                                </div>
                                <span className={styles.tabName} title={customerLabel}>{customerLabel}</span>
                            </div>

                            {/* only drafts can be cancelled here, and only with access */}
                            {hasCancelAccess && isDraft && (
                                <button
                                    type="button"
                                    className={styles.tabClose}
                                    aria-label={`Cancel ${invoiceNumber}`}
                                    title="Cancel draft"
                                    onClick={(event) => { event.stopPropagation(); handleDeleteDraft(event, draftId); }}
                                >
                                    <X size={13} strokeWidth={2.5} />
                                </button>
                            )}
                        </div>
                    );
                })}

                {sortedDrafts.length === 0 && <span className={styles.emptyText}>No saved drafts</span>}
            </div>

            <div className={styles.stripIdentity}>
                <span className={styles.stripLabel}>Open Bills</span>
                <span className={styles.draftCount}>{sortedDrafts.length}</span>
            </div>
        </div>
    );
};

export default BillActionStrip;